import React, { ReactNode, useMemo, useState } from 'react';

import { Box } from '@mui/material';
import { GBPCurrency } from '@apigrowfinance/pbolt-icons';
import IllustratedMessage from '@apigrowfinance/pbolt-illustrated-message';
import { Panel, PanelContent, PanelTitle } from '@apigrowfinance/pbolt-panel';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell } from 'recharts';

import LoadingErrorMessage from '@components/LoadingErrorMessage/LoadingErrorMessage';
import CustomLegend from '@components/overviewPanels/existingLoanPanel/Legend';
import { useAccount } from '@contexts/AccountProvider';

export interface LoanValue {
    name: ReactNode;
    value: number;
    id: string;
}

interface ExistingLoanPanelProps {
    title: ReactNode;
    isLoading: boolean;
    dataToPlot: LoanValue[];
    legendValuesNotPlotted?: LoanValue[];
    loanId?: string;
    isError?: boolean;
}

const ExistingLoanPanel = ({
    title,
    dataToPlot,
    legendValuesNotPlotted,
    isLoading,
    loanId,
    isError = false,
}: ExistingLoanPanelProps) => {
    const { theme } = useTheme();
    const { program } = useAccount();
    const [isHovered, setIsHovered] = useState(false);
    const navigate = useNavigate();
    const { account } = useAccount();
    const { formatMessage } = useIntl();

    const COLORS = [
        theme.palette.primary.main,
        theme.palette.info.main,
        theme.palette.text.primary,
    ];

    const data = dataToPlot.map((a) => {
        return { ...a };
    });
    let somethingToPlot = true;
    if (isLoading) {
        data.push({ value: 100, name: 'loader', id: 'loader' });
    } else {
        somethingToPlot = data.some((anItem) => anItem.value > 0);
    }

    const ariaLabel = useMemo(
        () =>
            formatMessage({
                id: 'ExistingLoanPanel.viewLoan',
                defaultMessage: 'View loan',
            }),
        [formatMessage],
    );

    const viewLoan = () => navigate(`/${account?.account_id}/loan`);

    return (
        <Panel
            onClick={isLoading || loanId === undefined ? null : viewLoan}
            isHovered={setIsHovered}
            ariaLabel={ariaLabel}>
            <PanelTitle
                loading={isLoading}
                hovered={isHovered}
                addPadding
                hideArrows={loanId === undefined}
                arrowLabel={
                    <FormattedMessage
                        id="ExistingLoanPanel.viewLoan"
                        defaultMessage="View loan"
                    />
                }>
                {title}
            </PanelTitle>
            <PanelContent>
                {isError && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        margin="auto"
                        data-testid="repayment-panel-error">
                        <LoadingErrorMessage />
                    </Box>
                )}
                {!isLoading && loanId === undefined && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        margin="auto"
                        data-testid="repayment-panel-error">
                        <IllustratedMessage
                            icon={<GBPCurrency color="inherit" />}
                            messageMain={
                                <FormattedMessage
                                    id="ExistingLoanPanel.noLoan"
                                    defaultMessage="No existing loan product"
                                />
                            }
                            messageSub={
                                <FormattedMessage
                                    id="ExistingLoanPanel.noLoansFound"
                                    defaultMessage="No existing loan products were found for this account"
                                />
                            }
                        />
                    </Box>
                )}
                {!isError && loanId && (
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            minWidth: theme.spacing(35),
                        }}>
                        <Box display="flex" flexDirection="row">
                            <Box
                                minHeight={theme.spacing(15)}
                                display="flex"
                                mt={1.75}>
                                <PieChart
                                    height={200}
                                    width={200}
                                    data-testid="chart"
                                    margin={{
                                        top: 0,
                                        right: 0,
                                        bottom: 0,
                                        left: 0,
                                    }}>
                                    <Pie
                                        isAnimationActive={!isLoading}
                                        stroke=""
                                        data={data}
                                        dataKey="value"
                                        nameKey="name"
                                        outerRadius={80}
                                        innerRadius={74}
                                        fill="#f00">
                                        {data.map((entry, index) => (
                                            <Cell
                                                key={`cell-${entry?.id.toLowerCase()}`}
                                                fill={
                                                    COLORS[
                                                        index % COLORS.length
                                                    ]
                                                }
                                            />
                                        ))}
                                    </Pie>
                                    {!somethingToPlot && (
                                        <Pie
                                            dataKey="value"
                                            outerRadius={60}
                                            innerRadius={54}
                                            stroke=""
                                            data={[
                                                {
                                                    name: 'No Data',
                                                    value: 1,
                                                    id: 'no-data',
                                                },
                                            ]}
                                            fill={COLORS[0]}
                                        />
                                    )}
                                </PieChart>
                            </Box>
                            <CustomLegend
                                isLoading={isLoading}
                                loans={data}
                                legendValuesNotPlotted={legendValuesNotPlotted}
                                colors={COLORS}
                                currencyCode={program?.currency_numeric_code}
                            />
                        </Box>
                    </Box>
                )}
            </PanelContent>
        </Panel>
    );
};
export default ExistingLoanPanel;
